import { type ComponentProps } from 'react';
import { NumberInput } from '@/components/ui/number-input';

interface CurrencyInputProps extends Omit<
  ComponentProps<typeof NumberInput>,
  'prefix'
> {
  currency?: string;
  symbol?: string;
  locale?: string;
}

const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  CNY: '¥',
  INR: '₹',
  CAD: '$',
  AUD: '$',
};

const currencyFormatterCache = new Map<string, Intl.NumberFormat>();

function getCurrencyFormatter(
  locale: string,
  currency: string
): Intl.NumberFormat {
  const key = `${locale}|${currency}`;
  let formatter = currencyFormatterCache.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, { style: 'currency', currency });
    currencyFormatterCache.set(key, formatter);
  }
  return formatter;
}

function resolveCurrencySymbol(
  currency: string,
  locale: string,
  symbol: string | undefined
): string {
  if (symbol) return symbol;
  if (CURRENCY_SYMBOLS[currency]) return CURRENCY_SYMBOLS[currency];

  try {
    const parts = getCurrencyFormatter(locale, currency).formatToParts(0);
    return parts.find((part) => part.type === 'currency')?.value || currency;
  } catch {
    return currency;
  }
}

export function CurrencyInput({
  currency = 'EUR',
  symbol,
  locale = 'en-US',
  decimalPlaces = 2,
  ...props
}: CurrencyInputProps) {
  const currencySymbol = resolveCurrencySymbol(currency, locale, symbol);

  return (
    <NumberInput
      prefix={currencySymbol}
      decimalPlaces={decimalPlaces}
      {...props}
    />
  );
}
